import * as React from 'react';

import { ScrollbarProps, Scrollbars } from 'react-custom-scrollbars';

import { cn } from '../../../lib';
import { ColorScheme } from '../../../types/theme';

const Thumb = (direction: 'vertical' | 'horizontal') => () => (
  <div className={direction === 'vertical' ? 'py-1' : 'px-1'}>
    <div className={'size-full rounded-xl bg-neutral'} />
  </div>
);

type EdgeShadowProps = {
  show: boolean;
  location: 'top' | 'bottom';
  themeMode: ColorScheme;
};

const EdgeShadow = ({ show, location, themeMode }: EdgeShadowProps) => (
  <div
    className={cn(
      '@EdgeShadow',
      cn(
        '@ScrollableEdgeShadow z-10 h-[0] transition duration-500',
        location === 'top' ? 'sticky inset-x-0 top-0' : 'relative w-full',
        show ? 'opacity-100' : 'opacity-0',
      ),
    )}
  >
    <div
      className={cn(
        'pointer-events-none absolute h-[80px] w-full to-transparent',
        themeMode === 'dark' && 'from-neutral/50',
        themeMode === 'light' && 'from-neutral',
        location === 'top' && 'bg-gradient-to-b',
        location === 'bottom' && 'bottom-0 bg-gradient-to-t',
      )}
    />
  </div>
);

const SCROLL_EDGE_THRESHOLD = 20;

export const Scrollable = React.forwardRef<
  Scrollbars,
  ScrollbarProps & {
    withEdgeShadows?: boolean;
    withTopEdgeShadow?: boolean;
    withBottomEdgeShadow?: boolean;
    themeMode: ColorScheme;
  }
>(
  (
    {
      className,
      children,
      withEdgeShadows,
      withTopEdgeShadow,
      withBottomEdgeShadow,
      themeMode,
      ...props
    },
    ref,
  ) => {
    const isAtTopEdgeRef = React.useRef(true);
    const isAtBottomEdgeRef = React.useRef(false);
    const [isAtTopEdge, setIsAtTopEdge] = React.useState(
      isAtTopEdgeRef.current,
    );
    const [isAtBottomEdge, setIsAtBottomEdge] = React.useState(
      isAtBottomEdgeRef.current,
    );

    const scrollListener = React.useCallback(
      (event: React.UIEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        const { scrollTop } = target;
        const atTopEdge = scrollTop < SCROLL_EDGE_THRESHOLD;
        const atBottomEdge =
          target.scrollHeight - target.offsetHeight - scrollTop <
          SCROLL_EDGE_THRESHOLD;

        if (atTopEdge !== isAtTopEdgeRef.current) {
          isAtTopEdgeRef.current = atTopEdge;
          requestAnimationFrame(() => setIsAtTopEdge(atTopEdge));
        }

        if (atBottomEdge !== isAtBottomEdgeRef.current) {
          isAtBottomEdgeRef.current = atBottomEdge;
          requestAnimationFrame(() => setIsAtBottomEdge(atBottomEdge));
        }
      },
      [],
    );

    return (
      <>
        <Scrollbars
          ref={ref}
          onScroll={scrollListener as never}
          {...props}
          autoHide
          autoHideTimeout={1000}
          className={cn('@Scrollable relative', className)}
          renderThumbVertical={Thumb('vertical')}
          renderThumbHorizontal={Thumb('horizontal')}
        >
          {(withEdgeShadows || withTopEdgeShadow) && (
            <EdgeShadow
              location="top"
              show={!isAtTopEdge}
              themeMode={themeMode}
            />
          )}
          {children}
        </Scrollbars>
        {(withEdgeShadows || withBottomEdgeShadow) && (
          <EdgeShadow
            location="bottom"
            show={!isAtBottomEdge}
            themeMode={themeMode}
          />
        )}
      </>
    );
  },
);
